import { createContext, memo, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { v1 } from "uuid";

export type ExpectedMessageProps = {
  messageKey?: string,
}

export type MimicMessageContextProps<T extends ExpectedMessageProps> = {
  push: (props: T) => T,
  remove: (key: string | undefined) => void,
  clear: () => void,
}

export type MimicMessageProviderProps<T extends ExpectedMessageProps> = {
  children: ReactNode | ReactNode[],
  onPush?: (message: T) => void
  onRemove?: (key?: string) => void
  onClear?: () => void
}

export function createMimicMessageContext<T extends ExpectedMessageProps>(modifyMessageOnPush: (allProps: T, userProps: T) => T = v => v) {
  const controlsContext = createContext<MimicMessageContextProps<T>>({
    clear: () => {
      throw new Error("Not implemented");
    },
    remove: key => {
      throw new Error("Not implemented");
    },
    push: props => {
      throw new Error("Not implemented");
    },
  });

  const messagesContext = createContext<T[]>([]);

  const MessageProvider = memo<MimicMessageProviderProps<T>>(({ children, onPush, onRemove, onClear }) => {
    const messageStorage = useMemo(() => ({ messages: [] as T[] }), []);
    const [ messagesOnUI, setMessagesOnUI ] = useState<T[]>(messageStorage.messages);

    const push = useCallback((props: T) => {
      const existingIndex = messageStorage.messages.findIndex((item) => item.messageKey === props.messageKey);
      const index = existingIndex !== -1 ? existingIndex : messageStorage.messages.length;

      const newMessageProps: T = modifyMessageOnPush({
        ...messageStorage.messages[existingIndex],
        ...props,
        messageKey: props.messageKey || v1(),
      }, props);

      const newMessages = [
        ...messageStorage.messages.slice(0, index),
        newMessageProps,
        ...messageStorage.messages.slice(index + 1),
      ];

      Object.freeze(newMessageProps);
      Object.freeze(newMessages);

      messageStorage.messages = newMessages;
      setMessagesOnUI(newMessages);

      onPush?.(newMessageProps);

      return newMessageProps;
    }, [ messageStorage, setMessagesOnUI, onPush ]);

    const remove = useCallback(
      (key: string | undefined) => {
        const newMessages = messageStorage.messages.filter((props) => props.messageKey !== key);
        Object.freeze(newMessages);
        messageStorage.messages = newMessages;
        setMessagesOnUI(newMessages);

        onRemove?.(key);
      },
      [ messageStorage, setMessagesOnUI, onRemove ],
    );

    const clear = useCallback(
      () => {
        const newMessages = [] as T[];
        Object.freeze(newMessages);
        messageStorage.messages = newMessages;
        setMessagesOnUI(newMessages);

        onClear?.();
      },
      [ messageStorage, setMessagesOnUI, onClear ],
    );

    const providerValue: MimicMessageContextProps<T> = useMemo(
      () => ({
        push,
        remove,
        clear,
      }),
      [ push, remove, clear ],
    );

    return (
      <controlsContext.Provider value={providerValue}>
        <messagesContext.Provider value={messagesOnUI}>
          {children}
        </messagesContext.Provider>
      </controlsContext.Provider>
    );
  });

  const useControls = () => useContext(controlsContext);
  const useMessages = () => useContext(messagesContext);

  return {
    controlsContext,
    messagesContext,
    MessageProvider,
    useControls,
    useMessages,
  };
}

